import { useState, useRef } from 'react';
import { Upload, X, FileImage, FileText, Loader2, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FileUpload({ 
  endpoint, 
  currentUrl, 
  onUploadSuccess, 
  onDeleteSuccess,
  label = "Görsel Yükle",
  acceptTypes = "image/*,.pdf"
}) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Dosya boyutu kontrolü (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan büyük olamaz');
      return;
    }

    // Dosya türü kontrolü
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Geçersiz dosya türü. İzin verilenler: JPG, PNG, GIF, WEBP, PDF');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Yükleme hatası');
      }

      const result = await response.json();
      toast.success('Dosya başarıyla yüklendi');
      
      if (onUploadSuccess) {
        onUploadSuccess(result.url);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Dosya yüklenirken hata oluştu');
    } finally {
      setUploading(false);
      // Input'u temizle
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!currentUrl) return;

    try {
      const deleteEndpoint = endpoint.replace('/upload/', '/upload/');
      const response = await fetch(`${BACKEND_URL}${deleteEndpoint}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Silme hatası');
      }

      toast.success('Dosya silindi');
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Dosya silinirken hata oluştu');
    }
  };

  const openPreview = () => {
    if (currentUrl) {
      window.open(`${BACKEND_URL}${currentUrl}`, '_blank');
    }
  };

  const isPdf = currentUrl?.endsWith('.pdf');

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptTypes}
        onChange={handleFileSelect}
        className="hidden"
        id={`file-upload-${endpoint}`}
      />
      
      {currentUrl ? (
        <div className="border border-border rounded-lg p-3 bg-surfaceSubtle">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isPdf ? (
                <FileText className="h-8 w-8 text-red-500" />
              ) : (
                <FileImage className="h-8 w-8 text-blue-500" />
              )}
              <span className="text-sm text-textPrimary truncate max-w-[150px]">
                {isPdf ? 'PDF Dosyası' : 'Görsel'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={openPreview}
                className="h-8 w-8 p-0"
                title="Görüntüle"
              >
                <Eye className="h-4 w-4 text-blue-500" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                className="h-8 w-8 p-0"
                title="Sil"
              >
                <X className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          </div>
          {!isPdf && (
            <img 
              src={`${BACKEND_URL}${currentUrl}`} 
              alt="Yüklenen görsel" 
              className="mt-2 max-h-32 rounded object-cover"
            />
          )}
        </div>
      ) : (
        <label
          htmlFor={`file-upload-${endpoint}`}
          className={`
            flex flex-col items-center justify-center w-full h-24 
            border-2 border-dashed border-border rounded-lg 
            cursor-pointer hover:bg-surfaceSubtle transition-colors
            ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {uploading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm text-textSecondary">Yükleniyor...</span>
            </div>
          ) : (
            <>
              <Upload className="h-6 w-6 text-textSecondary mb-1" />
              <span className="text-sm text-textSecondary">{label}</span>
              <span className="text-xs text-textSecondary mt-1">JPG, PNG, PDF (max 10MB)</span>
            </>
          )}
        </label>
      )}
    </div>
  );
}
